import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getAddHardwareFormData, createAsset } from '../../../../models/assetsModel';
import Modal from '../../../../components/common/Modal';

function AddHardwareModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    asset_tag: '',
    asset_name: '',
    asset_vendor_id: '',
    asset_processor: '',
    asset_serial: '',
    asset_storage: '',
    asset_state_id: '',
    asset_ram: '',
    asset_description: '',
    asset_cost: '',
    asset_date_purchased: '',
    asset_category_id: '',
    asset_ownership: '1',
    asset_commencement: '',
    asset_termination: ''
  });
  const [formOptions, setFormOptions] = useState({
    categories: [],
    vendors: [],
    states: [],
    ram: [],
    storage: [],
    processor: []
  });
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchFormData();
    }
  }, [isOpen]);

  const fetchFormData = async () => {
    try {
      setLoading(true);
      const data = await getAddHardwareFormData();
      if (data) {
        setFormOptions({
          categories: data.categories || [],
          vendors: data.vendors || [],
          states: data.states || [],
          ram: data.ram || [],
          storage: data.storage || [],
          processor: data.processor || []
        });
      }
    } catch (error) {
      console.error('Error fetching form data:', error);
      toast.error('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.asset_name.trim()) {
      toast.error('Asset name is required');
      return;
    }
    if (!formData.asset_tag.trim()) {
      toast.error('Asset tag is required');
      return;
    }
    if (!formData.asset_serial.trim()) {
      toast.error('Asset serial number is required');
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await createAsset(formData);
      
      if (result) {
        onSuccess();
        resetForm();
      }
    } catch (error) {
      console.error('Error creating hardware asset:', error);
      toast.error('Failed to create hardware asset');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      asset_tag: '',
      asset_name: '',
      asset_vendor_id: '',
      asset_processor: '',
      asset_serial: '',
      asset_storage: '',
      asset_state_id: '',
      asset_ram: '',
      asset_description: '',
      asset_cost: '',
      asset_date_purchased: '',
      asset_category_id: '',
      asset_ownership: '1',
      asset_commencement: '',
      asset_termination: ''
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Add New Hardware Asset" size="lg">
        <div className="flex flex-col justify-center items-center h-48">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 text-sm">Loading form data...</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add New Hardware Asset" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Basic Information Section */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center mb-3">
            <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-sm font-semibold text-gray-900">Basic Information</h3>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                <span className="flex items-center">
                  <span className="text-red-500 mr-1">*</span>
                  Asset Name
                </span>
              </label>
              <input
                type="text"
                name="asset_name"
                value={formData.asset_name}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter asset name or model"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                <span className="flex items-center">
                  <span className="text-red-500 mr-1">*</span>
                  Asset Tag
                </span>
              </label>
              <input
                type="text"
                name="asset_tag"
                value={formData.asset_tag}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors font-mono"
                placeholder="Enter asset tag"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Asset Category
              </label>
              <select
                name="asset_category_id"
                value={formData.asset_category_id}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="">Select Category</option>
                {formOptions.categories.map((category) => (
                  <option key={category.category_id} value={category.category_id}>
                    {category.category_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Asset Ownership
              </label>
              <select
                name="asset_ownership"
                value={formData.asset_ownership}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="1">Owned</option>
                <option value="2">Rental</option>
              </select>
            </div>
          </div>

          <div className="mt-3">
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="asset_description"
              value={formData.asset_description}
              onChange={handleInputChange}
              rows={2}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Enter asset description"
            />
          </div>
        </div>

        {/* Classification Section */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
          <div className="flex items-center mb-3">
            <svg className="w-4 h-4 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            <h3 className="text-sm font-semibold text-gray-900">Classification & Status</h3>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Vendor
              </label>
              <select
                name="asset_vendor_id"
                value={formData.asset_vendor_id}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
              >
                <option value="">Select Vendor</option>
                {formOptions.vendors.map((vendor) => (
                  <option key={vendor.category_id} value={vendor.category_id}>
                    {vendor.category_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Condition
              </label>
              <select
                name="asset_state_id"
                value={formData.asset_state_id}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
              >
                <option value="">Select Condition</option>
                {formOptions.states.map((state) => (
                  <option key={state.category_id} value={state.category_id}>
                    {state.category_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Financial Information Section */}
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 border border-yellow-200">
          <div className="flex items-center mb-3">
            <svg className="w-4 h-4 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
            <h3 className="text-sm font-semibold text-gray-900">Financial & Purchase Details</h3>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Asset Cost
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 text-xs font-medium">R</span>
                </div>
                <input
                  type="number"
                  step="0.01"
                  name="asset_cost"
                  value={formData.asset_cost}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md pl-8 pr-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Date Purchased
              </label>
              <input
                type="date"
                name="asset_date_purchased"
                value={formData.asset_date_purchased}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Technical Specifications Section */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
          <div className="flex items-center mb-3">
            <svg className="w-4 h-4 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
            <h3 className="text-sm font-semibold text-gray-900">Technical Specifications</h3>
          </div>
          
          <div className="mb-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                <span className="flex items-center">
                  <span className="text-red-500 mr-1">*</span>
                  Asset Serial Number
                </span>
              </label>
              <input
                type="text"
                name="asset_serial"
                value={formData.asset_serial}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors font-mono"
                placeholder="Enter serial number"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Asset Processor
              </label>
              <select
                name="asset_processor"
                value={formData.asset_processor}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
              >
                <option value="">Select Processor</option>
                {formOptions.processor.map((processor) => (
                  <option key={processor.category_id} value={processor.category_id}>
                    {processor.category_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Storage/Measurement
              </label>
              <select
                name="asset_storage"
                value={formData.asset_storage}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
              >
                <option value="">Select Storage</option>
                {formOptions.storage.map((storage) => (
                  <option key={storage.category_id} value={storage.category_id}>
                    {storage.category_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                RAM
              </label>
              <select
                name="asset_ram"
                value={formData.asset_ram}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
              >
                <option value="">Select RAM</option>
                {formOptions.ram.map((ram) => (
                  <option key={ram.category_id} value={ram.category_id}>
                    {ram.category_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Rental Information Section - Only show when ownership is rental */}
        {formData.asset_ownership === '2' && (
          <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-4 border border-orange-200">
            <div className="flex items-center mb-3">
              <svg className="w-4 h-4 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a4 4 0 118 0v4m-4 12v-2m0 0V9m0 2h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-sm font-semibold text-gray-900">Rental Information</h3>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Commencement Date
                </label>
                <input
                  type="date"
                  name="asset_commencement"
                  value={formData.asset_commencement}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Termination Date
                </label>
                <input
                  type="date"
                  name="asset_termination"
                  value={formData.asset_termination}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                />
              </div>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 transition-colors shadow-sm"
          >
            <svg className="w-3 h-3 mr-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white inline" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </>
            ) : (
              <>
                <svg className="w-3 h-3 mr-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Create Asset
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default AddHardwareModal;
